import type {Tone} from '../ui/primitives';

/* ===========================================================================
   The domain, in one file.
   ---------------------------------------------------------------------------
   Everything here is plain data: no React, no server calls. `src/lib/api.ts`
   is the only thing that talks to the server (Postgres via `server/db/schema.ts`,
   which mirrors these shapes field-for-field), so the apps never fetch directly.
   =========================================================================== */

export type Purpose = 'ban' | 'cho-thue';

export type ListingType = 'can-ho' | 'nha-pho' | 'biet-thu' | 'dat-nen' | 'shophouse' | 'van-phong';

export type Direction =
  | 'dong'
  | 'tay'
  | 'nam'
  | 'bac'
  | 'dong-nam'
  | 'dong-bac'
  | 'tay-nam'
  | 'tay-bac';

export type Legal = 'so-hong' | 'so-do' | 'hop-dong' | 'dang-cho';

export type Furnishing = 'day-du' | 'co-ban' | 'tho';

/** draft → published → sold, plus a pause the owner controls. */
export type ListingStatus = 'nhap' | 'dang-ban' | 'da-ban' | 'tam-an';

export type Role = 'khach' | 'nguoi-ban';

/** Where a picture's bytes live. The UI never reads this — `media.ts` does. */
export type MediaRef =
  /** Drawn by `lib/art.ts` from a seed. Seed data ships no binaries. */
  | {source: 'art'; scene: SceneId; seed: string}
  /** Uploaded photo: bytes are in the browser's IndexedDB, not localStorage. */
  | {source: 'idb'; id: string}
  /** A plain external URL. Valid shape, unused by anything this app produces today. */
  | {source: 'url'; url: string};

export type SceneId =
  | 'phong-khach'
  | 'phong-ngu'
  | 'bep'
  | 'phong-tam'
  | 'ban-cong'
  | 'mat-tien'
  | 'san-vuon';

export const SCENE_LABEL: Record<SceneId, string> = {
  'ban-cong': 'Ban công',
  bep: 'Bếp',
  'mat-tien': 'Mặt tiền',
  'phong-khach': 'Phòng khách',
  'phong-ngu': 'Phòng ngủ',
  'phong-tam': 'Phòng tắm',
  'san-vuon': 'Sân vườn',
};

export type PhotoKind = 'anh' | '360';

export interface Photo {
  id: string;
  /** `360` photos are equirectangular (2:1) and open in the panorama viewer. */
  kind: PhotoKind;
  label: string;
  ref: MediaRef;
}

export interface Listing {
  id: string;
  /** Mã tin — what people quote on the phone. */
  code: string;
  title: string;
  purpose: Purpose;
  type: ListingType;
  province: string;
  district: string;
  address: string;
  /** VND — total for `ban`, per month for `cho-thue`. */
  price: number;
  /** m². */
  area: number;
  bedrooms: number;
  bathrooms: number;
  floors: number;
  direction: Direction;
  legal: Legal;
  furnishing: Furnishing;
  amenities: string[];
  description: string;
  photos: Photo[];
  /** Photo id used as the card image; falls back to the first photo. */
  coverId: string | null;
  status: ListingStatus;
  /** Profile id of whoever posted it. */
  ownerId: string;
  contactName: string;
  contactPhone: string;
  createdAt: string;
  updatedAt: string;
  views: number;
  /** Set by a moderator in a real deployment; seeded here. */
  verified: boolean;
}

export interface Profile {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: Role;
  createdAt: string;
  verified: boolean;
}

export type InquiryStatus = 'moi' | 'da-lien-he' | 'dong';

export interface Inquiry {
  id: string;
  listingId: string;
  /** Null when a signed-out visitor left their number. */
  fromId: string | null;
  name: string;
  phone: string;
  message: string;
  /** ISO date-time the buyer would like to visit, or ''. */
  visitAt: string;
  at: string;
  status: InquiryStatus;
}

/* ------------------------------------------------------------------ labels */

export const PURPOSE: Record<Purpose, string> = {
  ban: 'Bán',
  'cho-thue': 'Cho thuê',
};

export const LISTING_TYPE: Record<ListingType, string> = {
  'biet-thu': 'Biệt thự',
  'can-ho': 'Căn hộ',
  'dat-nen': 'Đất nền',
  'nha-pho': 'Nhà phố',
  shophouse: 'Shophouse',
  'van-phong': 'Văn phòng',
};

export const DIRECTION: Record<Direction, string> = {
  bac: 'Bắc',
  dong: 'Đông',
  'dong-bac': 'Đông Bắc',
  'dong-nam': 'Đông Nam',
  nam: 'Nam',
  tay: 'Tây',
  'tay-bac': 'Tây Bắc',
  'tay-nam': 'Tây Nam',
};

export const LEGAL: Record<Legal, string> = {
  'dang-cho': 'Đang chờ sổ',
  'hop-dong': 'Hợp đồng mua bán',
  'so-do': 'Sổ đỏ',
  'so-hong': 'Sổ hồng',
};

export const FURNISHING: Record<Furnishing, string> = {
  'co-ban': 'Nội thất cơ bản',
  'day-du': 'Nội thất đầy đủ',
  tho: 'Bàn giao thô',
};

export const LISTING_STATUS: Record<ListingStatus, {label: string; tone: Tone}> = {
  'da-ban': {label: 'Đã bán', tone: 'slate'},
  'dang-ban': {label: 'Đang đăng', tone: 'emerald'},
  nhap: {label: 'Bản nháp', tone: 'amber'},
  'tam-an': {label: 'Tạm ẩn', tone: 'slate'},
};

export const INQUIRY_STATUS: Record<InquiryStatus, {label: string; tone: Tone}> = {
  'da-lien-he': {label: 'Đã liên hệ', tone: 'sky'},
  dong: {label: 'Đã đóng', tone: 'slate'},
  moi: {label: 'Mới', tone: 'emerald'},
};

export const ROLE: Record<Role, string> = {
  khach: 'Người mua',
  'nguoi-ban': 'Người đăng tin',
};

/** Only published listings are public. Everything else is owner-only. */
export function isPublic(listing: Listing): boolean {
  return listing.status === 'dang-ban' || listing.status === 'da-ban';
}

/* --------------------------------------------------------------- taxonomy */

export const PROVINCES = ['TP Hồ Chí Minh', 'Hà Nội', 'Đà Nẵng', 'Bình Dương'];

export const DISTRICTS: Record<string, string[]> = {
  'Bình Dương': ['Thủ Dầu Một', 'Dĩ An', 'Thuận An'],
  'Đà Nẵng': ['Hải Châu', 'Sơn Trà', 'Ngũ Hành Sơn'],
  'Hà Nội': ['Ba Đình', 'Cầu Giấy', 'Tây Hồ', 'Long Biên'],
  'TP Hồ Chí Minh': ['Quận 1', 'Quận 3', 'Quận 7', 'Bình Thạnh', 'Gò Vấp', 'TP Thủ Đức'],
};

export function districtsOf(province: string): string[] {
  return DISTRICTS[province] ?? [];
}

export const AMENITIES = [
  'Hồ bơi',
  'Phòng gym',
  'Bãi xe ô tô',
  'An ninh 24/7',
  'Gần trường học',
  'Gần chợ / siêu thị',
  'Công viên nội khu',
  'Thang máy',
  'Ban công thoáng',
  'Cho nuôi thú cưng',
];

/** Price per m² — the number that makes two listings actually comparable. */
export function pricePerM2(listing: Listing): number {
  return listing.area > 0 ? listing.price / listing.area : 0;
}

export function has360(listing: Listing): boolean {
  return listing.photos.some((photo) => photo.kind === '360');
}

export function coverPhoto(listing: Listing): Photo | null {
  const cover = listing.photos.find((photo) => photo.id === listing.coverId);
  return cover ?? listing.photos[0] ?? null;
}
